import { useState } from 'react';
import { useGetAnalyticsQuery, useGetUserProgressListQuery } from '../../../Services/PathwayAdminAPI';
import { FaUsers, FaUserCheck, FaCheckCircle, FaListUl, FaSearch } from 'react-icons/fa';
import '../../../Styles/PathwayAnalytics.css';

const PathwayAnalytics = () => {
  const { data: analyticsData, isLoading: analyticsLoading, error: analyticsError } = useGetAnalyticsQuery();
  const { data: usersData, isLoading: usersLoading, error: usersError } = useGetUserProgressListQuery();
  const [searchTerm, setSearchTerm] = useState('');

  if (analyticsLoading || usersLoading) {
    return (
      <div className="pathway-analytics">
        <div className="loading-spinner">Loading analytics data...</div>
      </div>
    );
  }

  if (analyticsError || usersError) {
    return (
      <div className="pathway-analytics">
        <div className="error-message">
          Error loading analytics: {analyticsError?.message || usersError?.message || 'Unknown error'}
        </div>
      </div>
    );
  }

  const analytics = analyticsData?.data || {};
  const users = usersData?.data || [];

  // Extract nested data from API response
  const pathwayStructure = analytics.pathway_structure || {};
  const userEngagement = analytics.user_engagement || {};

  // Map API response to component variables
  const stats = {
    total_users: userEngagement.total_users || 0,
    active_users: userEngagement.total_users || 0, // Using total_users as active_users for now
    completed_steps: userEngagement.total_completed_steps || 0,
    total_steps: pathwayStructure.total_steps || 0
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.user_id?.toString().includes(searchTerm.toLowerCase()) ||
    user.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.current_step?.step_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate completion percentage
  const completionPercentage = stats.total_steps > 0
    ? Math.round((stats.completed_steps / stats.total_steps) * 100)
    : 0;

  return (
    <div className="pathway-analytics">
      <div className="analytics-header">
        <h3>Pathway Analytics</h3>
        <p className="analytics-subtitle">Track user progress and completion statistics</p>
      </div>

      <div className="analytics-wrapper">
        {/* Overall Statistics */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon total-users">
              <FaUsers />
            </div>
            <div className="stat-content">
              <h4>{stats.total_users}</h4>
              <p>Total Users</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon active-users">
              <FaUserCheck />
            </div>
            <div className="stat-content">
              <h4>{stats.active_users}</h4>
              <p>Active Users</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon completed-steps">
              <FaCheckCircle />
            </div>
            <div className="stat-content">
              <h4>{stats.completed_steps}</h4>
              <p>Completed Steps</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon total-steps">
              <FaListUl />
            </div>
            <div className="stat-content">
              <h4>{stats.total_steps}</h4>
              <p>Total Steps</p>
            </div>
          </div>
        </div>

        {/* Progress Visualization */}
        <div className="progress-section">
          <h4>Overall Completion Progress</h4>
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${completionPercentage}%` }}
              >
                <span className="progress-text">{completionPercentage}%</span>
              </div>
            </div>
            <div className="progress-stats">
              <span>{stats.completed_steps} of {stats.total_steps} steps completed</span>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        {analytics.categories && analytics.categories.length > 0 && (
          <div className="category-breakdown">
            <h4>Progress by Category</h4>
            <div className="category-list">
              {analytics.categories.map((category, index) => {
                const categoryPercentage = category.total_steps > 0
                  ? Math.round((category.completed_steps / category.total_steps) * 100)
                  : 0;

                return (
                  <div key={index} className="category-progress-item">
                    <div className="category-progress-header">
                      <span className="category-name">{category.name}</span>
                      <span className="category-percentage">{categoryPercentage}%</span>
                    </div>
                    <div className="category-progress-bar">
                      <div
                        className="category-progress-fill"
                        style={{ width: `${categoryPercentage}%` }}
                      ></div>
                    </div>
                    <div className="category-progress-stats">
                      <span>{category.completed_steps} / {category.total_steps} steps</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* User Progress List */}
        <div className="users-section">
          <div className="users-header">
            <h4>User Progress</h4>
            <div className="search-box">
              <FaSearch />
              <input
                type="text"
                placeholder="Search by name, email, or step..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="users-table-container">
            {filteredUsers.length === 0 ? (
              <div className="empty-state">
                <p>{searchTerm ? 'No users found matching your search.' : 'No user progress data available.'}</p>
              </div>
            ) : (
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Current Step</th>
                    <th>Steps Attempted</th>
                    <th>Progress</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => {
                    const userPercentage = parseFloat(user.completion_percentage) || 0;
                    const displayName = user.user_name || `User #${user.user_id}`;
                    const avatarLetter = user.user_name
                      ? user.user_name.charAt(0).toUpperCase()
                      : user.user_id?.toString().charAt(0) || 'U';

                    return (
                      <tr key={index}>
                        <td>
                          <div className="user-info">
                            <div className="user-avatar">
                              {avatarLetter}
                            </div>
                            <span className="user-name">{displayName}</span>
                          </div>
                        </td>
                        <td className="user-email">
                          {user.user_email || 'N/A'}
                        </td>
                        <td>
                          {user.current_step?.step_title || 'N/A'}
                        </td>
                        <td className="steps-count">
                          {user.total_steps_attempted || 0} / {stats.total_steps}
                        </td>
                        <td>
                          <div className="table-progress-bar">
                            <div
                              className="table-progress-fill"
                              style={{ width: `${userPercentage}%` }}
                            ></div>
                          </div>
                          <span className="table-progress-text">{Math.round(userPercentage)}%</span>
                        </td>
                        <td className="last-activity">
                          {user.last_activity
                            ? new Date(user.last_activity).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })
                            : 'N/A'
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PathwayAnalytics;
